export type PlayStyle = 'casual' | 'balanced' | 'competitive' | 'teaching' | 'farming';
export type VoicePreference = 'required' | 'optional' | 'none';
export type SessionStatus = 'open' | 'full' | 'in_progress' | 'completed' | 'canceled';

export interface Progression {
  level: number;
  title: string;
  points: number;
  nextLevelAt: number | null;
  progress: number;
}

export interface Reputation {
  sessionsCompleted: number;
  reviewsReceived: number;
  playAgainRate: number | null;
  confirmedTeammates: number;
  tags: string[];
}

export interface PlayerProfile {
  id: string;
  username: string;
  displayName: string;
  avatar: string;
  avatarIcon: string | null;
  avatarColor: string | null;
  bio: string;
  pronouns: string | null;
  timezone: string;
  playStyle: PlayStyle;
  voice: VoicePreference;
  languages: string[];
  platforms: string[];
  availability: string[];
  games: string[];
  createdAt: string;
  progression: Progression;
  reputation: Reputation;
  linkedProviders: string[];
}

export interface Activity {
  id: string;
  slug: string;
  name: string;
  category: string;
  defaultDurationMinutes: number;
}

export interface Game {
  id: string;
  slug: string;
  name: string;
  tagline: string;
  accent: string | null;
  maxParty: number;
  roles: string[];
  platforms: string[];
  activities: Activity[];
}

export interface SessionSummary {
  guild: { slug: string; name: string; tag: string | null } | null;
  repeatsWeekly: boolean;
  waitlistCount: number;
  id: string;
  game: string;
  gameName: string;
  gameAccent: string | null;
  activity: string;
  title: string;
  description: string;
  startTime: string;
  durationMinutes: number;
  partySize: number;
  currentPlayers: number;
  openSpots: number;
  readyCount: number;
  requiredRoles: string[];
  platform: string;
  voice: string;
  visibility: string;
  style: string;
  language: string;
  status: SessionStatus;
  hostId: string;
  hostUsername: string;
  hostAvatar: string;
  hostAvatarIcon: string | null;
  hostAvatarColor: string | null;
  createdAt: string;
  discordInviteUrl: string | null;
}

export interface Participant {
  userId: string;
  username: string;
  avatar: string;
  avatarIcon: string | null;
  avatarColor: string | null;
  role: string | null;
  ready: boolean;
  isHost: boolean;
}

export interface SessionDetailPayload {
  session: SessionSummary;
  participants: Participant[];
  waitlist: Array<{ userId: string; username: string; avatar: string }>;
  review: { opensAt: string; closesAt: string; reviewedUserIds: string[] };
  viewer: { id: string; joined: boolean; waitlisted: boolean; isHost: boolean } | null;
}

export type GuildRole = 'owner' | 'officer' | 'member';

export interface GuildSummary {
  slug: string;
  name: string;
  tag: string | null;
  description: string;
  joinPolicy: string;
  memberCount: number;
  upcomingCount: number;
  myRole: GuildRole | null;
}

export interface GuildMember {
  userId: string;
  username: string;
  displayName: string;
  avatar: string;
  role: GuildRole;
}

export interface GuildPost {
  id: string;
  body: string;
  createdAt: string;
  authorUsername: string;
}

export interface GuildDetailPayload {
  guild: {
    slug: string;
    name: string;
    tag: string | null;
    description: string;
    joinPolicy: string;
    discordInviteUrl: string | null;
    createdAt: string;
  };
  members: GuildMember[];
  posts: GuildPost[];
  sessions: SessionSummary[];
  viewer: {
    role: GuildRole | null;
    pending: boolean;
    canPost: boolean;
    canManageMembers: boolean;
    canManageGuild: boolean;
    pendingRequests: number;
  };
}

export interface AuthUser {
  id: string;
  email: string;
  username: string;
}

export interface PendingReview {
  session: SessionSummary;
  teammates: Array<{ userId: string; username: string; avatar: string }>;
  closesAt: string;
}

export interface MeSummary {
  profile: PlayerProfile;
  upcoming: SessionSummary[];
  pendingReviews: PendingReview[];
  unreadNotifications: number;
}

export interface AppNotification {
  id: string;
  kind: string;
  title: string;
  body: string;
  link: string | null;
  read: boolean;
  createdAt: string;
}

export interface CompatibilityResult {
  score: number;
  level: 'Perfect fit' | 'Great fit' | 'Good fit' | 'Okay fit' | 'Low fit';
  reasons: string[];
  warnings: string[];
}

export const PLAY_STYLE_LABELS: Record<PlayStyle, string> = {
  casual: 'Casual',
  balanced: 'Balanced',
  competitive: 'Competitive',
  teaching: 'Teaching',
  farming: 'Farming'
};

export const VOICE_LABELS: Record<VoicePreference, string> = {
  required: 'Voice required',
  optional: 'Voice optional',
  none: 'No voice'
};

export const AVAILABILITY_OPTIONS: Array<{ slug: string; label: string }> = [
  { slug: 'weekday-mornings', label: 'Weekday mornings' },
  { slug: 'weekday-afternoons', label: 'Weekday afternoons' },
  { slug: 'weeknights', label: 'Weeknights' },
  { slug: 'late-night', label: 'Late night' },
  { slug: 'weekend-mornings', label: 'Weekend mornings' },
  { slug: 'weekend-afternoons', label: 'Weekend afternoons' },
  { slug: 'weekend-evenings', label: 'Weekend evenings' }
];

export const COMMEND_TAGS = ['Chill', 'Great comms', 'Skilled', 'Patient teacher', 'Reliable', 'Funny', 'Leader', 'Clutch'];
